import { useState, type FormEvent } from "react";
import { Card, Title, Text } from "@tremor/react";
import { UserPlus, Trash2, KeyRound, Check, X, Lock, RotateCw, Plus, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth, useChangePassword } from "@/lib/auth";
import {
  useUsers, useCreateUser, useUpdateUser, useResetPassword, useDeactivateUser,
  useSecrets, useUpsertSecret, useDeleteSecret,
  useTenants, useCreateTenant, useUpdateTenant, useRetryGraylogSetup,
  type PanelUser, type TenantSecret, type Tenant,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";

type TabId = "profile" | "tenant" | "users" | "secrets" | "notifications" | "rules";

interface TabDef {
  id: TabId;
  label: string;
  adminOnly?: boolean;
}

const TABS: TabDef[] = [
  { id: "profile",       label: "Profile" },
  { id: "tenant",        label: "Tenant",        adminOnly: true },
  { id: "users",         label: "Users",         adminOnly: true },
  { id: "secrets",       label: "Secrets",       adminOnly: true },
  { id: "notifications", label: "Notifications" },
  { id: "rules",         label: "Detection rules" },
];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState<TabId>("profile");
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Tenant configuration, integrations, and account management.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        {TABS.filter((t) => !t.adminOnly || isAdmin).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t.id
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "profile"       && <ProfileTab />}
      {tab === "tenant"  && isAdmin && <TenantTab />}
      {tab === "users"   && isAdmin && <UsersTab />}
      {tab === "secrets" && isAdmin && <SecretsTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "rules"         && <RulesTab />}
    </div>
  );
}

// ---- Profile tab ----------------------------------------------------------

function ProfileTab() {
  const { user } = useAuth();
  const change = useChangePassword();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);

  const localError =
    next && next.length < 8 ? "New password must be at least 8 characters." :
    next && confirm && next !== confirm ? "New passwords don't match." :
    null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSuccess(false);
    if (localError) return;
    try {
      await change.mutateAsync({ current_password: current, new_password: next });
      setSuccess(true);
      setCurrent(""); setNext(""); setConfirm("");
    } catch { /* mutation surfaces error below */ }
  }

  let serverError: string | null = null;
  if (change.isError) {
    serverError = change.error instanceof ApiError && change.error.status === 401
      ? "Current password is incorrect."
      : "Couldn't change password. Try again.";
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Card>
        <Title>Account</Title>
        <Text className="mt-2">Logged-in user details.</Text>
        <div className="mt-4 grid grid-cols-[120px,1fr] gap-y-2 text-sm">
          <div className="text-muted-foreground">Email</div>
          <div className="font-mono text-xs">{user?.email}</div>
          <div className="text-muted-foreground">Name</div>
          <div>{user?.full_name ?? "—"}</div>
          <div className="text-muted-foreground">Role</div>
          <div className="font-mono text-xs">{user?.role}</div>
          <div className="text-muted-foreground">Tenant</div>
          <div className="font-mono text-xs">{user?.tenant_id}</div>
        </div>
      </Card>

      <Card>
        <Title>Change password</Title>
        <Text className="mt-2">
          Other active sessions for your account get revoked when you change your password.
        </Text>
        <form onSubmit={onSubmit} className="mt-4 space-y-3">
          <PasswordField label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" />
          <PasswordField label="New password" value={next} onChange={setNext} autoComplete="new-password" />
          <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" />

          {(localError || serverError) && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
              {localError ?? serverError}
            </div>
          )}
          {success && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
              Password changed.
            </div>
          )}

          <button
            type="submit"
            disabled={!current || !next || !confirm || !!localError || change.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {change.isPending ? "Saving…" : "Update password"}
          </button>
        </form>
      </Card>
    </div>
  );
}

function PasswordField({
  label, value, onChange, autoComplete,
}: {
  label: string; value: string; onChange: (v: string) => void; autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium">{label}</span>
      <input
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </label>
  );
}

// ---- Users tab (admin only) ----------------------------------------------

function UsersTab() {
  const q = useUsers();
  const tenantsQ = useTenants();
  const [showInvite, setShowInvite] = useState(false);
  // Multi-tenant: only show the Tenant column when the caller can see
  // more than one tenant. Single-tenant admins (Cambridge later) won't
  // see a useless column repeating their own tenant id on every row.
  const showTenantColumn = (tenantsQ.data?.items?.length ?? 0) > 1;

  return (
    <div className="space-y-4">
      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <div className="text-sm font-semibold">
              {showTenantColumn ? "Users (all tenants)" : "Tenant users"}
            </div>
            <div className="text-xs text-muted-foreground">
              {q.data ? `${q.data.total} total` : "Loading…"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowInvite((v) => !v)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus className="h-4 w-4" />
            {showInvite ? "Cancel" : "Add user"}
          </button>
        </div>

        {showInvite && (
          <div className="border-b border-border bg-muted/20 px-4 py-4">
            <InviteForm onDone={() => setShowInvite(false)} />
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Email</th>
              {showTenantColumn && <th className="px-4 py-3">Tenant</th>}
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last login</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {q.data?.items.map((u) => (
              <UserRow key={u.id} user={u} showTenant={showTenantColumn} />
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function InviteForm({ onDone }: { onDone: () => void }) {
  const create = useCreateUser();
  const { user: me } = useAuth();
  // /tenants returns all tenants for platform admins (NRG-tenant admins)
  // and only the caller's own for tenant-scoped admins. We hide the
  // selector entirely when there's only one option to keep the form tidy.
  const tenantsQ = useTenants();
  const tenants = tenantsQ.data?.items ?? [];
  const showTenantPicker = tenants.length > 1;

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<"admin" | "operator" | "viewer">("admin");
  const [tenantId, setTenantId] = useState<string>(me?.tenant_id ?? "");

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        email: email.trim(),
        password,
        full_name: fullName || null,
        role,
        // Only send tenant_id when the picker is visible and the user
        // chose something different from their own tenant; backend default
        // handles the same-tenant case so the wire shape stays minimal.
        tenant_id: showTenantPicker && tenantId !== me?.tenant_id ? tenantId : undefined,
      });
      setEmail(""); setFullName(""); setPassword(""); setRole("admin");
      setTenantId(me?.tenant_id ?? "");
      onDone();
    } catch { /* error shows below */ }
  }

  let errorText: string | null = null;
  if (create.isError) {
    errorText = create.error instanceof ApiError && create.error.status === 409
      ? "A user with that email already exists."
      : create.error instanceof ApiError && create.error.status === 403
      ? "You don't have permission to create users in that tenant."
      : create.error instanceof ApiError && create.error.status === 400
      ? "That tenant doesn't exist."
      : "Couldn't create user. Try again.";
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Email" value={email} onChange={setEmail} type="email" required />
        <Field label="Full name (optional)" value={fullName} onChange={setFullName} />
        <Field label="Initial password" value={password} onChange={setPassword} type="password" required minLength={8} />
        <label className="block">
          <span className="block text-xs font-medium">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="admin">admin (full access)</option>
            <option value="operator">operator (read + triage)</option>
            <option value="viewer">viewer (read only)</option>
          </select>
        </label>
        {showTenantPicker && (
          <label className="block md:col-span-2">
            <span className="block text-xs font-medium">Tenant</span>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name} <span className="text-muted-foreground">({t.id})</span>
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Pick the tenant this user belongs to. Defaults to yours
              ({me?.tenant_id}); change it to onboard a customer admin.
            </p>
          </label>
        )}
      </div>

      {errorText && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-400">
          {errorText}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={create.isPending || !email || password.length < 8}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {create.isPending ? "Creating…" : "Create user"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function Field({
  label, value, onChange, type = "text", required, minLength,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; required?: boolean; minLength?: number;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium">{label}</span>
      <input
        type={type}
        required={required}
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </label>
  );
}

function UserRow({ user: u, showTenant }: { user: PanelUser; showTenant: boolean }) {
  const update = useUpdateUser();
  const reset = useResetPassword();
  const deactivate = useDeactivateUser();
  const { user: me } = useAuth();
  const isSelf = me?.id === u.id;

  const [showReset, setShowReset] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  async function onResetSubmit() {
    if (newPw.length < 8) return;
    await reset.mutateAsync({ id: u.id, new_password: newPw });
    setNewPw(""); setShowReset(false);
  }

  async function onToggleActive() {
    await update.mutateAsync({ id: u.id, body: { is_active: !u.is_active } });
  }

  return (
    <>
      <tr className="border-b border-border last:border-0 hover:bg-muted/30">
        <td className="px-4 py-3 font-mono text-xs">
          {u.email}
          {isSelf && <span className="ml-2 rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">you</span>}
        </td>
        {showTenant && (
          <td className="px-4 py-3">
            <span className="rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-[11px]">
              {u.tenant_id}
            </span>
          </td>
        )}
        <td className="px-4 py-3">{u.full_name ?? "—"}</td>
        <td className="px-4 py-3">
          <select
            value={u.role}
            disabled={isSelf}
            onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value as PanelUser["role"] } })}
            className="rounded-md border border-input bg-background px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="admin">admin</option>
            <option value="operator">operator</option>
            <option value="viewer">viewer</option>
          </select>
        </td>
        <td className="px-4 py-3">
          {u.is_active ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
              <Check className="h-3.5 w-3.5" /> active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <X className="h-3.5 w-3.5" /> inactive
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">
          {u.last_login_at ? new Date(u.last_login_at).toLocaleString([], {
            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
          }) : "never"}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              onClick={() => setShowReset((v) => !v)}
              title="Reset password"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              <KeyRound className="h-3.5 w-3.5" />
            </button>
            {!isSelf && (
              u.is_active ? (
                <button
                  type="button"
                  onClick={() => setConfirmDeactivate(true)}
                  title="Deactivate"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onToggleActive}
                  title="Reactivate"
                  className="rounded-md border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:bg-accent"
                >
                  Reactivate
                </button>
              )
            )}
          </div>
        </td>
      </tr>

      {showReset && (
        <tr className="border-b border-border bg-muted/20">
          <td colSpan={showTenant ? 7 : 6} className="px-4 py-3">
            <div className="flex items-center gap-2">
              <input
                type="password"
                placeholder="New password (min 8 chars)"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <button
                type="button"
                disabled={newPw.length < 8 || reset.isPending}
                onClick={onResetSubmit}
                className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {reset.isPending ? "Saving…" : "Reset"}
              </button>
              <button
                type="button"
                onClick={() => { setShowReset(false); setNewPw(""); }}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
              >
                Cancel
              </button>
            </div>
            <div className="mt-1 text-[11px] text-muted-foreground">
              All of {u.email}'s active sessions will be invalidated.
            </div>
          </td>
        </tr>
      )}

      {confirmDeactivate && (
        <tr className="border-b border-border bg-red-500/5">
          <td colSpan={showTenant ? 7 : 6} className="px-4 py-3">
            <div className="flex items-center gap-3 text-sm">
              <span>Deactivate <strong>{u.email}</strong>? They'll be signed out and unable to log back in until reactivated.</span>
              <button
                type="button"
                onClick={async () => { await deactivate.mutateAsync(u.id); setConfirmDeactivate(false); }}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
              >
                Confirm deactivate
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeactivate(false)}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent"
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---- Notifications + Rules tabs (placeholders) ----------------------------

// ---- Tenant tab (admin only) ----------------------------------------------

function TenantTab() {
  const { user } = useAuth();
  const tenantsQ = useTenants();
  const update = useUpdateTenant();
  const create = useCreateTenant();

  const myTenant: Tenant | undefined = tenantsQ.data?.items?.find(
    (t) => t.id === user?.tenant_id,
  );

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<{
    display_name: string;
    telegram_chat_id: string;
    audit_cron: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  if (tenantsQ.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!myTenant) {
    return (
      <Card>
        <Title>Tenant</Title>
        <Text className="mt-2">
          Your user's <code className="font-mono">tenant_id</code> doesn't have
          a matching <code className="font-mono">tenants</code> row. Run a SQL
          backfill or contact support.
        </Text>
      </Card>
    );
  }

  const startEdit = () => {
    setEditing({
      display_name: myTenant.display_name,
      telegram_chat_id: "",
      audit_cron: myTenant.audit_cron,
    });
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditing(null);
    setError(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: myTenant.id,
        body: {
          display_name: editing.display_name.trim() || undefined,
          telegram_chat_id: editing.telegram_chat_id, // empty string clears
          audit_cron: editing.audit_cron.trim() || undefined,
        },
      });
      setSuccess("Tenant updated.");
      setEditing(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <Title>This tenant</Title>
            <Text className="mt-2">
              Your tenant's display configuration. The tenant <em>id</em> is
              immutable — it's used as a key in many tables and as a path
              segment for the static webroot. To rename a tenant, create a
              new one and migrate the data.
            </Text>
          </div>
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm hover:bg-accent"
            >
              Edit
            </button>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
        {success && !error && (
          <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
            {success}
          </div>
        )}

        {editing ? (
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Display name</label>
              <input
                type="text"
                value={editing.display_name}
                onChange={(e) => setEditing({ ...editing, display_name: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Telegram digest chat id (optional)
              </label>
              <input
                type="text"
                placeholder="-1001234567890 (group) or 123456789 (DM)"
                value={editing.telegram_chat_id}
                onChange={(e) => setEditing({ ...editing, telegram_chat_id: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Leave empty to inherit the legacy single-chat fallback. Add the bot to the
                target chat first; ID via @userinfobot.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Daily audit cron (UTC)</label>
              <input
                type="text"
                value={editing.audit_cron}
                onChange={(e) => setEditing({ ...editing, audit_cron: e.target.value })}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Stored but not yet honored — every active tenant runs at 08:00 today.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={cancelEdit}
                className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={update.isPending}
                onClick={saveEdit}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {update.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-[160px,1fr] gap-y-2 text-sm">
            <div className="text-muted-foreground">ID</div>
            <div className="font-mono text-xs">{myTenant.id}</div>
            <div className="text-muted-foreground">Display name</div>
            <div>{myTenant.display_name}</div>
            <div className="text-muted-foreground">Status</div>
            <div className="font-mono text-xs">{myTenant.status}</div>
            <div className="text-muted-foreground">Telegram digest</div>
            <div className="text-xs">
              {myTenant.has_telegram_chat
                ? <span className="text-emerald-600 dark:text-emerald-400">configured</span>
                : <span className="text-muted-foreground">inheriting legacy fallback</span>}
            </div>
            <div className="text-muted-foreground">Webroot</div>
            <div className="font-mono text-xs">/var/www/tyrael/{myTenant.webroot_subdir}</div>
            <div className="text-muted-foreground">Audit cron</div>
            <div className="font-mono text-xs">{myTenant.audit_cron}</div>
            <div className="text-muted-foreground">Graylog stream</div>
            <div className="text-xs">
              <GraylogStatusInline tenant={myTenant} />
            </div>
            <div className="text-muted-foreground">Created</div>
            <div className="text-xs">{new Date(myTenant.created_at).toLocaleString()}</div>
          </div>
        )}
      </Card>

      {(tenantsQ.data?.items?.length ?? 0) > 1 && (
        <Card>
          <Title>All tenants — Graylog provisioning</Title>
          <Text className="mt-2">
            What Tyrael set up in Graylog for each tenant. Stream
            routing fires on the <code className="font-mono">tenant_id</code>{" "}
            field — filebeat shippers tag every message with that field
            automatically. Raw syslog from network devices (Hikvision,
            MikroTik) needs a per-source-IP pipeline rule that adds
            tenant_id at ingest; that's a per-device follow-up, not a
            per-tenant one.
          </Text>
          <div className="mt-4 space-y-3">
            {tenantsQ.data?.items?.map((t) => (
              <TenantGraylogPanel key={t.id} tenant={t} />
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <Title>Add new tenant</Title>
            <Text className="mt-2">
              Bootstrap a new customer tenant. Creates the <code className="font-mono">tenants</code> row;
              you'll then need to create at least one user under it via psql or the users
              endpoint with the new <code className="font-mono">tenant_id</code> set.
            </Text>
          </div>
          {!showAdd && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" /> Add tenant
            </button>
          )}
        </div>

        {showAdd && (
          <NewTenantForm
            onCancel={() => setShowAdd(false)}
            onSubmit={async (b) => {
              try {
                await create.mutateAsync(b);
                setShowAdd(false);
                setSuccess(`Tenant '${b.id}' created.`);
                setError(null);
              } catch (e) {
                setError(e instanceof Error ? e.message : "Create failed");
              }
            }}
            submitting={create.isPending}
          />
        )}
      </Card>
    </div>
  );
}

// Single-line Graylog status for the tenant-info row. Three states:
//   - never attempted (legacy NRG, pre-feature) → grey
//   - failed                                     → red + error
//   - ok                                         → green link to Graylog
function GraylogStatusInline({ tenant }: { tenant: Tenant }) {
  if (!tenant.graylog_setup_at) {
    return <span className="text-muted-foreground">not provisioned (legacy)</span>;
  }
  if (tenant.graylog_setup_error || !tenant.graylog_stream_url) {
    return (
      <span className="text-red-600 dark:text-red-400">
        setup failed — see Tenants overview below
      </span>
    );
  }
  return (
    <a
      href={tenant.graylog_stream_url}
      target="_blank"
      rel="noopener noreferrer"
      className="font-mono text-xs text-primary hover:underline"
    >
      {tenant.graylog_stream_id} ↗
    </a>
  );
}

// Per-tenant Graylog panel: shows what Tyrael did (stream + rules) on
// success, the error + retry button on failure. Used in the platform-
// admin overview below the personal tenant card.
function TenantGraylogPanel({ tenant }: { tenant: Tenant }) {
  const retry = useRetryGraylogSetup();
  const summary = tenant.graylog_setup_summary;
  const ok = !!tenant.graylog_stream_id && !tenant.graylog_setup_error;

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{tenant.id}</span>
            <span className="text-xs text-muted-foreground">{tenant.display_name}</span>
            {ok ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
                <Check className="h-3 w-3" /> provisioned
              </span>
            ) : tenant.graylog_setup_at ? (
              <span className="rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] text-red-700 dark:text-red-400">
                failed
              </span>
            ) : (
              <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                not attempted
              </span>
            )}
          </div>

          {ok && summary && (
            <div className="mt-2 space-y-1.5 text-[11px]">
              <div>
                <span className="text-muted-foreground">Stream: </span>
                <a
                  href={tenant.graylog_stream_url ?? "#"}
                  target="_blank" rel="noopener noreferrer"
                  className="font-mono text-primary hover:underline"
                >
                  {summary.title} ↗
                </a>
              </div>
              <div className="text-muted-foreground">Routing rules:</div>
              <ul className="ml-4 list-disc space-y-0.5">
                {summary.rules.map((r, i) => (
                  <li key={i} className="font-mono text-[11px]">
                    {r.field} = {r.value}{" "}
                    <span className="text-muted-foreground">(exact match)</span>
                  </li>
                ))}
              </ul>
              <div className="text-muted-foreground">
                Provisioned {tenant.graylog_setup_at && new Date(tenant.graylog_setup_at).toLocaleString()}
              </div>
            </div>
          )}

          {!ok && tenant.graylog_setup_error && (
            <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/5 p-2 text-[11px] text-red-700 dark:text-red-400">
              <div className="font-medium">Last setup error:</div>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                {tenant.graylog_setup_error}
              </pre>
              <div className="mt-1 text-muted-foreground">
                Last attempt: {tenant.graylog_setup_at && new Date(tenant.graylog_setup_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {!ok && (
          <button
            type="button"
            disabled={retry.isPending}
            onClick={() => retry.mutate(tenant.id)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent disabled:opacity-50"
          >
            <RotateCw className={cn("h-3.5 w-3.5", retry.isPending && "animate-spin")} />
            {retry.isPending ? "Retrying…" : "Retry setup"}
          </button>
        )}
      </div>
    </div>
  );
}

function NewTenantForm({
  onCancel, onSubmit, submitting,
}: {
  onCancel: () => void;
  onSubmit: (body: {
    id: string;
    display_name: string;
    telegram_chat_id: string;
    webroot_subdir: string;
    audit_cron: string;
  }) => void;
  submitting: boolean;
}) {
  const [id, setId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [telegram, setTelegram] = useState("");
  const [webroot, setWebroot] = useState("");
  const [cron, setCron] = useState("0 8 * * *");

  const idValid = /^[a-z][a-z0-9_-]{1,30}$/.test(id);

  return (
    <form
      onSubmit={(e) => { e.preventDefault();
        if (!idValid || !displayName.trim()) return;
        onSubmit({
          id,
          display_name: displayName.trim(),
          telegram_chat_id: telegram,
          webroot_subdir: webroot,
          audit_cron: cron,
        });
      }}
      className="mt-4 space-y-3 rounded-md border border-border bg-muted/20 p-4"
    >
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Tenant id <span className="text-amber-600">(immutable)</span>
        </label>
        <input
          type="text"
          required
          value={id}
          onChange={(e) => setId(e.target.value.toLowerCase())}
          placeholder="cambridge"
          className={cn(
            "w-full rounded-md border bg-background px-3 py-1.5 font-mono text-sm",
            id && !idValid ? "border-red-500/50" : "border-border",
          )}
        />
        <p className="mt-1 text-[11px] text-muted-foreground">
          Lowercase letters, digits, hyphen, underscore. Starts with a letter, 2-31 chars.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Display name</label>
        <input
          type="text" required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Cambridge School Constanta"
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Telegram digest chat id (optional)
        </label>
        <input
          type="text"
          value={telegram}
          onChange={(e) => setTelegram(e.target.value)}
          placeholder="-1001234567890"
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Webroot subdir (defaults to tenant id)
        </label>
        <input
          type="text"
          value={webroot}
          onChange={(e) => setWebroot(e.target.value)}
          placeholder={id || "<tenant-id>"}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Daily audit cron (UTC)
        </label>
        <input
          type="text"
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !idValid || !displayName.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Building2 className="h-4 w-4" />
          {submitting ? "Creating…" : "Create tenant"}
        </button>
      </div>
    </form>
  );
}

// ---- Secrets tab -----------------------------------------------------------

function SecretsTab() {
  const q = useSecrets();
  const upsert = useUpsertSecret();
  const del = useDeleteSecret();

  const [editing, setEditing] = useState<TenantSecret | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = q.data?.items ?? [];

  const onSubmit = async (vals: { name: string; value: string; description: string }) => {
    setError(null);
    try {
      await upsert.mutateAsync({
        name:        vals.name,
        value:       vals.value,
        description: vals.description || null,
      });
      setEditing(null);
      setCreating(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    }
  };

  const onDelete = async (id: number) => {
    if (!confirm("Delete this secret? Connectors that reference it will start failing.")) return;
    try {
      await del.mutateAsync(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Title>Tenant secrets</Title>
          <Text className="mt-2">
            Encrypted credential store. Connector configs reference these
            by name (e.g. <code>username_ref: "hikvision/admin/user"</code>);
            the runner resolves them to plaintext only inside the server
            process. Values are never returned to the panel — to rotate,
            re-enter the value here.
          </Text>
        </div>
        <button
          type="button"
          onClick={() => { setCreating(true); setEditing(null); }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> New secret
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {(creating || editing) && (
        <SecretForm
          existing={editing}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSubmit={onSubmit}
          submitting={upsert.isPending}
        />
      )}

      <div className="mt-4 space-y-2">
        {q.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
        {!q.isLoading && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <Lock className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No secrets yet. Add one when an http_poll connector needs
              credentials, then reference it from the connector config.
            </p>
          </div>
        )}
        {items.map((s) => (
          <SecretRow
            key={s.id}
            s={s}
            onEdit={() => { setEditing(s); setCreating(false); }}
            onDelete={() => onDelete(s.id)}
          />
        ))}
      </div>
    </Card>
  );
}

function SecretRow({
  s, onEdit, onDelete,
}: {
  s: TenantSecret;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-card px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-mono text-sm">{s.name}</span>
        </div>
        {s.description && (
          <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>
        )}
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Created {new Date(s.created_at).toLocaleString()}
          {s.rotated_at && (
            <> · rotated {new Date(s.rotated_at).toLocaleString()}</>
          )}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          onClick={onEdit}
          title="Rotate value"
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
        >
          <RotateCw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete"
          className="rounded-md p-1.5 text-red-600 hover:bg-red-500/10 dark:text-red-400"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function SecretForm({
  existing, onCancel, onSubmit, submitting,
}: {
  existing: TenantSecret | null;
  onCancel: () => void;
  onSubmit: (v: { name: string; value: string; description: string }) => void;
  submitting: boolean;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState(existing?.description ?? "");

  const onSubmitForm = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !value) return;
    onSubmit({ name: name.trim(), value, description: description.trim() });
  };

  return (
    <form onSubmit={onSubmitForm} className="mt-4 space-y-3 rounded-md border border-border bg-muted/20 p-4">
      <h3 className="text-sm font-semibold">
        {existing ? `Rotate "${existing.name}"` : "New secret"}
      </h3>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!!existing}
          placeholder="hikvision/admin/password"
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm disabled:opacity-60"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Value {existing && "(entering a new value rotates the secret)"}
        </label>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="••••••••"
          autoComplete="new-password"
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Description (optional)</label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Hikvision MinMoe terminals — admin password"
          className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !name.trim() || !value}
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {submitting ? "Saving…" : existing ? "Rotate" : "Save"}
        </button>
      </div>
    </form>
  );
}

function NotificationsTab() {
  return (
    <Card>
      <Title>Notifications</Title>
      <Text className="mt-2">
        How critical alerts reach you. Telegram is wired up; SMS uses
        SMSLink and falls back when Telegram is unreachable.
      </Text>
      <div className="mt-4 space-y-2 text-sm">
        <SettingRow label="Telegram digest"      state="enabled" />
        <SettingRow label="SMS on critical"      state="disabled" />
        <SettingRow label="Daily PDF report"     state="on request" />
      </div>
    </Card>
  );
}

function RulesTab() {
  return (
    <Card>
      <Title>Detection rules</Title>
      <Text className="mt-2">
        Toggle which behavioural rules run on flows from your network.
      </Text>
      <div className="mt-4 space-y-2 text-sm">
        <SettingRow label="port_scan"    state="enabled" />
        <SettingRow label="threat_intel" state="enabled" />
        <SettingRow label="geo_anomaly"  state="enabled" />
        <SettingRow label="beacon"       state="enabled" />
      </div>
    </Card>
  );
}

function SettingRow({ label, state }: { label: string; state: "enabled" | "disabled" | "on request" }) {
  const tone =
    state === "enabled"  ? "text-emerald-600" :
    state === "disabled" ? "text-muted-foreground" :
                           "text-amber-600";
  return (
    <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
      <span>{label}</span>
      <span className={`text-xs font-medium ${tone}`}>{state}</span>
    </div>
  );
}
